import { Modal } from 'antd';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import ImageWithBasePath from '../../../core/common/imageWithBasePath';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { fetchPayslipsByEmployeeId } from '../../../core/data/redux/payroll/payrollSalary/payslip/payslipActions';
import { useAppConfig } from '../../../customhooks/useAppConfig';
import { formatCurrency } from '../../../core/utils/configFormatters';

const PayslipModal: React.FC<any> = ({ isPaySlipModalVisible, setIsPaySlipModalVisible, selectedRowData }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { payslipsByEmployee } = useSelector((state: any) => state.payroll.salaryPayslip);
    useEffect(() => {
        if (selectedRowData?.employee_id) {
            dispatch(fetchPayslipsByEmployeeId(selectedRowData.employee_id));
        }
    }, [selectedRowData, dispatch]);

    const payslip = payslipsByEmployee;

    const config = useAppConfig();
    if (!config) return null;

    return (
        <div>
            <div
                className={`modal fade ${isPaySlipModalVisible ? 'show' : ''}`}
                aria-hidden={isPaySlipModalVisible ? 'false' : 'true'}
                style={{ display: isPaySlipModalVisible ? 'block' : 'none' }}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        {/* Modal Header with Close Button */}
                        <div className="modal-header text-white">
                            <h4 className="modal-title">Payslip</h4>
                            <button
                                type="button"
                                className="btn-close custom-btn-close"
                                onClick={() => setIsPaySlipModalVisible(false)}
                            >
                                <i className="ti ti-x" />
                            </button>
                        </div>

                        {payslip ? (
                            <div className="p-4" id="payslip-content">
                                <div className="content">
                                    <h4 className="modal-title text-center">Payslip</h4>
                                    {/* Payslip Header */}
                                    <div className="row justify-content-between align-items-center mb-2">
                                        <div className="col-md-6">
                                            <div className="mb-2">
                                                <ImageWithBasePath
                                                    src="assets/img/logo.svg"
                                                    className="img-fluid"
                                                    alt="logo"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <h5 className="text-dark mb-1">
                                                Payslip No: <span className="text-success">{payslip.payslip_number}</span>
                                            </h5>
                                            <p className="fw-medium">
                                                Salary Month: <span className="text-dark">{payslip.month_name} {payslip.year}</span>
                                            </p>
                                        </div>
                                    </div>

                                    {/* Employee Details */}
                                    <div className="row border-bottom align-items-center my-3 pb-2">
                                        <div className="col-md-6 d-flex align-items-center">
                                            <div>
                                                <h4 className="mb-1 text-info">{payslip.employee_name}</h4>
                                                <p className="mb-1">Employee Id: {payslip.employee_number}</p>
                                                <p>Email: <span className="text-dark">{payslip.employee_email}</span></p>
                                            </div>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <p><strong>Generated On: </strong>{new Date(payslip.generated_date).toLocaleDateString()}</p>
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* First Column: Earnings and Deductions */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                {/* Earnings Section */}
                                                <div className="list-group my-2">
                                                    <div className="list-group-item bg-light p-3 border-bottom-0">
                                                        <h6 className="text-secondary">Earnings</h6>
                                                    </div>
                                                    {payslip.earnings_json &&
                                                        payslip.earnings_json.map((earning: any) => (
                                                            <div className="list-group-item" key={earning.id}>
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <p className="mb-0 text-muted">{earning.name}</p>
                                                                    <h6 className="fw-medium text-success">{formatCurrency(earning?.amount, config)}</h6>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    <div className="list-group-item">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <p className="mb-0 text-muted">Total Earnings</p>
                                                            <h6 className="fw-medium text-success">
                                                                {formatCurrency(payslip?.total_earnings, config)}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                {/* Deductions Section */}
                                                <div className="list-group my-2">
                                                    <div className="list-group-item bg-light p-3 border-bottom-0">
                                                        <h6 className="text-secondary">Deductions</h6>
                                                    </div>
                                                    {payslip?.deductions_json &&
                                                        payslip.deductions_json.map((deduction: any) => (
                                                            <div className="list-group-item" key={deduction.id}>
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <p className="mb-0 text-muted">{deduction.name}</p>
                                                                    <h6 className="fw-medium text-danger">{formatCurrency(deduction?.proratedAmount, config)}</h6>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    {payslip?.leave_deduction > 0 && (
                                                        <div className="list-group-item">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <p className="mb-0 text-muted">Leave Deduction</p>
                                                                <h6 className="fw-medium text-danger">
                                                                    {formatCurrency(payslip.leave_deduction, config)}
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="list-group-item">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <p className="mb-0 text-muted">Total Deductions</p>
                                                            <h6 className="fw-medium text-danger">
                                                                {formatCurrency(
                                                                    (Number(payslip?.total_deductions || 0) + Number(payslip?.leave_deduction || 0)),
                                                                    config
                                                                )}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Second Column: Attendance and Overtime Information */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                {/* Attendance and Leave Information */}
                                                <div className="list-group my-2">
                                                    <div className="list-group-item bg-light p-3 border-bottom-0">
                                                        <h6 className="text-secondary">Attendance & Leave Information</h6>
                                                    </div>
                                                    <div className="list-group-item">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <p className="mb-0">Total Attendance Count</p>
                                                            <h6 className="fw-medium text-dark">{payslip.total_attendance_count}</h6>
                                                        </div>
                                                    </div>
                                                    <div className="list-group-item">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <p className="mb-0">Leaves Taken</p>
                                                            <h6 className="fw-medium text-dark">{payslip.leaves_taken}</h6>
                                                        </div>
                                                    </div>
                                                    <div className="list-group-item">
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <p className="mb-0">Balance Leave</p>
                                                            <h6 className="fw-medium text-dark">{payslip.balance_leave}</h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                {/* Overtime Information */}
                                                {payslip?.overtime_hours > 0 || payslip?.overtime_pay > 0 ? (
                                                    <div className="list-group my-2">
                                                        <div className="list-group-item bg-light p-3 border-bottom-0">
                                                            <h6 className="text-secondary">Overtime Information</h6>
                                                        </div>
                                                        {payslip.overtime_hours > 0 && (
                                                            <div className="list-group-item">
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <p className="mb-0">Overtime Hours</p>
                                                                    <h6 className="fw-medium text-info">{payslip.overtime_hours}</h6>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {payslip.overtime_pay > 0 && (
                                                            <div className="list-group-item">
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                     
                                                                    <p className="mb-0">Overtime Pay</p>
                                                                    <h6 className="fw-medium text-info">{formatCurrency(payslip?.overtime_pay, config)}</h6>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Net Salary Section */}
                                    <div className="text-center my-4">
                                        <p>
                                            <div className="list-group-item bg-light p-3 border-bottom-0 d-flex justify-content-between">
                                                <h6 className="text-secondary">Net Salary</h6>
                                                <h6 className="text-secondary">{formatCurrency(payslip?.net_salary, config)}</h6>
                                            </div>
                                        </p>
                                    </div>
                                    {/* Footer */}
                                    <div className="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3">
                                        <p className="mb-0 text-muted">2025© hrmtree.</p>
                                        <p>
                                            Developed By{" "}
                                            <Link to="#" className="text-primary">
                                                Leadchain Systems
                                            </Link>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-center text-muted">Loading payslip details...</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayslipModal;
